import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import Label from "@components/form/Label";
import PhoneInput from "@components/form/PhoneInput";
import TextInput from "@components/form/TextInput";
import { useAppContext } from "@context/AppContext";
import { requestUpdateUser } from "@services/user";
import FormDialog from "./FormDialog";

/** Subscribers can't edit their own details; this asks an operator to. */
const RequestUpdateDialog: FunctionComponent<{
  onClose: () => void;
  onSent: () => void;
}> = ({ onClose, onSent }) => {
  const { t } = useTranslation();
  const {
    loggedInInfo: { userDetails },
  } = useAppContext();
  const requiredMessage = t("common.required.text") as string;
  const schema = z.object({
    name: z.string().min(1, requiredMessage),
    email: z.string().email(t("common.required.email") as string),
    tel: z.string({ required_error: requiredMessage }).min(1, requiredMessage),
  });
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: userDetails?.name ?? "",
      email: userDetails?.email ?? "",
      tel: userDetails?.tel ?? "",
    },
  });
  const { mutate, isLoading, isError } = useMutation(requestUpdateUser, {
    onSuccess: onSent,
  });

  return (
    <FormDialog
      title={t("profile.request_update")}
      submitLabel={t("common.confirm")}
      onClose={onClose}
      onSubmit={handleSubmit(values => mutate(values))}
      busy={isLoading}
      error={isError && t("profile.request_update_failure")}
    >
      <div>
        <Label htmlFor="request-name">{t("user.username")}</Label>
        <TextInput
          id="request-name"
          {...register("name")}
          errorMessage={errors.name?.message}
        />
      </div>
      <div>
        <Label htmlFor="request-email">{t("user.email")}</Label>
        <TextInput
          id="request-email"
          type="email"
          {...register("email")}
          errorMessage={errors.email?.message}
        />
      </div>
      <div>
        <Label htmlFor="request-tel">{t("user.tel")}</Label>
        <Controller
          name="tel"
          control={control}
          render={({ field, fieldState }) => (
            <PhoneInput
              id="request-tel"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>
    </FormDialog>
  );
};

export default RequestUpdateDialog;
